/* Compare Sizes — PDP drawer controller.
   Opens over the PDP (right drawer / mobile bottom sheet) from
   snippets/compare-sizes-link.liquid. One size is shown at a time across
   all products (chips switch it); products render as columns with a delta
   vs the base product on every measurement row.
   Data: /products/<handle>?view=compare-data (same payload + sessionStorage
   cache as compare-sizes.js). Selection state: window.CompareStore — shared
   with the /pages/compare-sizes fallback page, one item per handle, cap 4. */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var dl = '{' + '{';
    var dr = '}' + '}';
    return format
      .replace(dl + 'amount' + dr, amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(dl + 'amount_no_decimals' + dr, Math.round(cents / 100).toString());
  }

  /* Predictive-search prices arrive as plain amounts in the session's
     presentment currency — theme.moneyFormat is the shop's default and would
     stamp the wrong symbol on localized sessions, so prefer Intl + the
     active currency. */
  function formatSearchPrice(price) {
    var n = parseFloat(price);
    if (isNaN(n)) return '';
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (currency && window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(n);
      } catch (e) { /* unknown code — fall through */ }
    }
    return formatMoney(Math.round(n * 100));
  }

  /* ---------- compare-data fetch (cache shared with compare-sizes.js) ---------- */

  var CACHE_PREFIX = 'pwt_csz_data:';
  var CACHE_TTL = 10 * 60 * 1000;

  function cacheGet(handle) {
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.fetchedAt > CACHE_TTL) return null;
      return entry.payload;
    } catch (e) { return null; }
  }

  function cacheSet(handle, payload) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify({ fetchedAt: Date.now(), payload: payload }));
    } catch (e) { /* no-op */ }
  }

  function fetchProduct(handle) {
    var cached = cacheGet(handle);
    if (cached) return Promise.resolve(cached);
    return fetch('/products/' + encodeURIComponent(handle) + '?view=compare-data', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var payload = JSON.parse(text);
        cacheSet(handle, payload);
        return payload;
      });
  }

  /* ---------- measurement helpers (ported from compare-sizes.js) ---------- */

  /* header strings vary per product ("sleeve" vs "sleeve length") - alias to
     canonical keys so cross-product rows align (keep in sync with
     compare-sizes.js) */
  var HEADER_ALIASES = {
    'sleeve length': 'sleeve',
    'sleeve body length': 'sleeve',
    'body length': 'length',
    'bust': 'chest'
  };

  function normHeader(h) {
    var n = String(h || '').toLowerCase().replace(/\s+/g, ' ').trim();
    return HEADER_ALIASES[n] || n;
  }

  function stripParens(h) {
    var n = normHeader(h).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    return HEADER_ALIASES[n] || n;
  }

  function normSize(s) {
    return String(s || '').toLowerCase().trim();
  }

  /* dataset for a payload given tab + unit → {headers, rows} | null */
  function dataset(payload, tab, unit) {
    if (!payload || !payload.sizeChart) return null;
    var block = tab === 'product' ? payload.sizeChart.garment : payload.sizeChart.body;
    if (!block) return null;
    var ds = unit === 'cm' ? block.centimeters : block.inches;
    if (!ds || !ds.headers || !ds.headers.length || !ds.rows) return null;
    return ds;
  }

  function rowForSize(ds, size) {
    if (!ds) return null;
    for (var i = 0; i < ds.rows.length; i++) {
      if (normSize(ds.rows[i][0]) === normSize(size)) return ds.rows[i];
    }
    return null;
  }

  /* header-matched cell value from a product's row, or null */
  function valueFor(ds, row, header) {
    if (!ds || !row) return null;
    var lookup = {};
    ds.headers.forEach(function (h, idx) {
      if (idx === 0) return;
      lookup[normHeader(h)] = row[idx];
      var stripped = stripParens(h);
      if (!(stripped in lookup)) lookup[stripped] = row[idx];
    });
    var v = lookup[normHeader(header)];
    if (v == null || v === '') v = lookup[stripParens(header)];
    if (v == null || v === '') return null;
    return v;
  }

  /* strict: "23.75" yes, "39-40" / "23 ½" no — deltas on ranges mislead */
  function toNumber(v) {
    var s = String(v).replace(',', '.').trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    return parseFloat(s);
  }

  function formatDelta(value, baseValue) {
    var v = toNumber(value);
    var b = toNumber(baseValue);
    if (v == null || b == null) return null;
    var d = Math.round((v - b) * 100) / 100;
    if (d === 0) return '0';
    return (d > 0 ? '+' : '−') + String(Math.abs(d));
  }

  function garmentHeaders(payload) {
    var g = payload && payload.sizeChart && payload.sizeChart.garment;
    if (!g) return null;
    var ds = g.inches || g.centimeters;
    if (!ds || !ds.headers || ds.headers.length < 2) return null;
    return ds.headers.slice(1);
  }

  /* comparable = the two garment charts share at least one measurement column */
  function sharesGarmentHeader(a, b) {
    var ha = garmentHeaders(a);
    var hb = garmentHeaders(b);
    if (!ha || !hb) return false;
    var seen = {};
    ha.forEach(function (h) { seen[stripParens(h)] = true; });
    return hb.some(function (h) { return seen[stripParens(h)]; });
  }

  function variantForSize(payload, size) {
    if (!payload || !payload.variants) return null;
    var match = null;
    var avail = null;
    payload.variants.forEach(function (v) {
      if (normSize(v.size) !== normSize(size)) return;
      if (!match) match = v;
      if (!avail && v.available) avail = v;
    });
    return avail || match;
  }

  /* ---------- drawer controller ---------- */

  function CompareDrawer(root, config) {
    this.root = root;
    this.panel = qs('.csz-drawer__panel', root);
    this.config = config;
    this.state = {
      open: false,
      view: 'table',       /* 'table' | 'picker' */
      tab: 'product',      /* 'product' | 'body' */
      unit: 'in',
      size: null,          /* normalized size key shown across all products */
      data: {},            /* handle -> payload */
      failed: {},          /* handle -> true */
      loading: false
    };
    this._returnFocus = null;
    this._searchTimer = null;
    this._searchSeq = 0;
    this.bind();
  }

  CompareDrawer.prototype.baseHandle = function () {
    return this.config.baseHandle;
  };

  CompareDrawer.prototype.basePayload = function () {
    return this.state.data[this.baseHandle()] || null;
  };

  /* items to show as columns: store items minus the base product itself */
  CompareDrawer.prototype.items = function () {
    var base = this.baseHandle();
    return window.CompareStore.get().items.filter(function (it) { return it.handle !== base; });
  };

  CompareDrawer.prototype.atCap = function () {
    return window.CompareStore.count() >= window.CompareStore.MAX_ITEMS;
  };

  /* ---------- open / close ---------- */

  CompareDrawer.prototype.open = function (trigger) {
    if (this.state.open) return;
    var self = this;
    this.state.open = true;
    this._returnFocus = trigger || document.activeElement;

    window.CompareStore.setBase(this.baseHandle());
    this.state.size = null; /* re-derived from the PDP once data is in */
    this.showView('table');

    this.root.hidden = false;
    /* double rAF so the transition runs from the hidden state */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (self.state.open) self.root.classList.add('is-open');
      });
    });
    document.documentElement.classList.add('csz-drawer-open');

    var closeBtn = qs('.csz-drawer__close', this.root);
    if (closeBtn) closeBtn.focus();

    this.load();
  };

  CompareDrawer.prototype.close = function () {
    if (!this.state.open) return;
    var self = this;
    this.state.open = false;
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('csz-drawer-open');
    window.setTimeout(function () {
      if (!self.state.open) self.root.hidden = true;
    }, 420);
    if (this._returnFocus && this._returnFocus.focus) {
      try { this._returnFocus.focus(); } catch (e) { /* trigger may be gone */ }
    }
    this._returnFocus = null;
  };

  /* ---------- data loading ---------- */

  CompareDrawer.prototype.load = function () {
    var self = this;
    var handles = [this.baseHandle()];
    this.items().forEach(function (it) {
      if (handles.indexOf(it.handle) === -1) handles.push(it.handle);
    });

    var missing = handles.filter(function (h) { return !self.state.data[h]; });
    if (missing.length) {
      this.state.loading = true;
      var table = qs('[data-csz-table]', this.root);
      if (table && !table.childNodes.length) {
        table.innerHTML = '<p class="csz-drawer__loading">loading measurements…</p>';
      }
    }

    Promise.all(missing.map(function (h) {
      return fetchProduct(h).then(
        function (payload) { self.state.data[h] = payload; delete self.state.failed[h]; },
        function () { self.state.failed[h] = true; }
      );
    })).then(function () {
      self.state.loading = false;
      if (!self.state.open) return;
      self.render();
    });
  };

  /* ---------- size default ---------- */

  CompareDrawer.prototype.pdpSelectedSize = function () {
    var checked = document.querySelector('[data-section-type="product"] .variant-input-wrap[data-handle="size"] input[data-variant-input]:checked');
    if (checked && checked.value) return checked.value;
    return this.config.defaultSize || null;
  };

  CompareDrawer.prototype.baseSizes = function () {
    var base = this.basePayload();
    var ds = dataset(base, this.state.tab, this.state.unit);
    if (ds) return ds.rows.map(function (r) { return String(r[0] || ''); });
    return ((base && base.variants) || []).map(function (v) { return v.size; }).filter(function (s, i, arr) {
      return arr.indexOf(s) === i;
    });
  };

  CompareDrawer.prototype.ensureSize = function () {
    var sizes = this.baseSizes();
    var keys = sizes.map(normSize);
    if (this.state.size && keys.indexOf(this.state.size) !== -1) return;

    var pdp = normSize(this.pdpSelectedSize());
    if (pdp && keys.indexOf(pdp) !== -1) {
      this.state.size = pdp;
      return;
    }
    /* fall back to the first size with an available variant, else first row */
    var base = this.basePayload();
    for (var i = 0; i < sizes.length; i++) {
      var v = variantForSize(base, sizes[i]);
      if (v && v.available) { this.state.size = normSize(sizes[i]); return; }
    }
    this.state.size = keys[0] || null;
  };

  /* ---------- render: comparison ---------- */

  CompareDrawer.prototype.render = function () {
    if (this.state.view !== 'table') { this.renderPicker(); return; }

    var base = this.basePayload();
    var table = qs('[data-csz-table]', this.root);

    if (this.state.failed[this.baseHandle()] && !base) {
      table.innerHTML = '';
      var err = document.createElement('p');
      err.className = 'csz-drawer__loading';
      err.textContent = 'we couldn’t load size data. ';
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'csz-drawer__foot-link';
      retry.textContent = 'try again';
      var self = this;
      retry.addEventListener('click', function () {
        delete self.state.failed[self.baseHandle()];
        self.load();
      });
      err.appendChild(retry);
      table.appendChild(err);
      return;
    }
    if (!base) return; /* still loading */

    /* if the active tab has no data for the base, switch to the other one */
    if (!dataset(base, this.state.tab, this.state.unit)) {
      var other = this.state.tab === 'product' ? 'body' : 'product';
      if (dataset(base, other, this.state.unit)) this.state.tab = other;
    }

    this.ensureSize();
    this.renderChips();
    this.renderControls();
    this.renderTable();
    this.renderAddButton();
    this.renderFootnote();
  };

  CompareDrawer.prototype.renderChips = function () {
    var self = this;
    var wrap = qs('[data-csz-chips]', this.root);
    var focused = document.activeElement && wrap.contains(document.activeElement)
      ? normSize(document.activeElement.textContent) : null;
    wrap.textContent = '';
    this.baseSizes().forEach(function (s) {
      var k = normSize(s);
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'csz-drawer__chip';
      chip.textContent = k;
      if (k === self.state.size) chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', k === self.state.size ? 'true' : 'false');
      chip.addEventListener('click', function () {
        if (self.state.size === k) return;
        self.state.size = k;
        self.render();
      });
      wrap.appendChild(chip);
      if (focused === k) chip.focus();
    });
  };

  CompareDrawer.prototype.renderControls = function () {
    var self = this;
    var base = this.basePayload();
    var hasGarment = !!dataset(base, 'product', this.state.unit);
    var hasBody = !!dataset(base, 'body', this.state.unit);

    qsa('[data-csz-tab]', this.root).forEach(function (btn) {
      var tab = btn.getAttribute('data-csz-tab');
      var active = tab === self.state.tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.disabled = (tab === 'product' && !hasGarment) || (tab === 'body' && !hasBody);
    });
    qsa('[data-csz-unit]', this.root).forEach(function (btn) {
      var unit = btn.getAttribute('data-csz-unit');
      var active = unit === self.state.unit;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.disabled = !dataset(base, 'product', unit) && !dataset(base, 'body', unit);
    });

    var notice = qs('[data-csz-notice]', this.root);
    notice.hidden = hasGarment;
    if (!hasGarment) notice.textContent = 'garment measurements aren’t available for this product yet.';
  };

  CompareDrawer.prototype.productCell = function (opts) {
    /* opts: { payload, title, priceFormatted, image, isBase, handle } */
    var cell = document.createElement('div');
    cell.className = 'csz-drawer__prod';

    var thumb = document.createElement('span');
    thumb.className = 'csz-drawer__prod-thumb';
    var imgSrc = opts.image || (opts.payload && opts.payload.image);
    if (imgSrc) {
      var img = document.createElement('img');
      img.src = imgSrc;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    cell.appendChild(thumb);

    var nameRow = document.createElement('span');
    nameRow.className = 'csz-drawer__prod-name-row';
    var name = document.createElement('span');
    name.className = 'csz-drawer__prod-name';
    var title = (opts.title || (opts.payload && opts.payload.title) || opts.handle || '').toLowerCase();
    name.textContent = title;
    name.title = title;
    nameRow.appendChild(name);

    if (!opts.isBase) {
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'csz-drawer__prod-remove';
      remove.setAttribute('aria-label', 'remove ' + title + ' from comparison');
      remove.innerHTML = '&#215;';
      remove.addEventListener('click', function () {
        window.CompareStore.removeProduct(opts.handle);
      });
      nameRow.appendChild(remove);
    }
    cell.appendChild(nameRow);

    var price = document.createElement('span');
    price.className = 'csz-drawer__prod-price';
    price.textContent = opts.priceFormatted ||
      (opts.payload && (opts.payload.priceFormatted || formatMoney(opts.payload.price || 0))) || '';
    cell.appendChild(price);

    if (opts.isBase) {
      var tag = document.createElement('span');
      tag.className = 'csz-drawer__prod-tag';
      tag.textContent = 'this product';
      cell.appendChild(tag);
    }
    return cell;
  };

  CompareDrawer.prototype.renderTable = function () {
    var self = this;
    var wrap = qs('[data-csz-table]', this.root);
    wrap.textContent = '';

    var base = this.basePayload();
    var baseDs = dataset(base, this.state.tab, this.state.unit);
    var baseRow = rowForSize(baseDs, this.state.size);
    var headers = baseDs ? baseDs.headers : [];
    var items = this.items();
    this._anyMissing = false;

    var table = document.createElement('table');
    table.className = 'csz-drawer__table';

    /* products row */
    var thead = document.createElement('thead');
    var headRow = document.createElement('tr');
    var spacer = document.createElement('th');
    spacer.setAttribute('scope', 'col');
    spacer.innerHTML = '<span class="visually-hidden">measurement</span>';
    headRow.appendChild(spacer);

    var thBase = document.createElement('th');
    thBase.setAttribute('scope', 'col');
    thBase.appendChild(this.productCell({
      payload: base,
      title: this.config.baseTitle,
      priceFormatted: this.config.basePriceFormatted,
      image: this.config.baseImage,
      isBase: true,
      handle: this.baseHandle()
    }));
    headRow.appendChild(thBase);

    items.forEach(function (it) {
      var th = document.createElement('th');
      th.setAttribute('scope', 'col');
      if (self.state.failed[it.handle] && !self.state.data[it.handle]) {
        th.appendChild(self.productCell({ payload: null, title: it.title, isBase: false, handle: it.handle }));
      } else {
        th.appendChild(self.productCell({
          payload: self.state.data[it.handle],
          title: it.title,
          isBase: false,
          handle: it.handle
        }));
      }
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    table.appendChild(thead);

    /* measurement rows, driven by the base product's headers */
    var tbody = document.createElement('tbody');
    headers.slice(1).forEach(function (header) {
      var tr = document.createElement('tr');
      var th = document.createElement('th');
      th.setAttribute('scope', 'row');
      th.textContent = normHeader(header);
      tr.appendChild(th);

      var baseVal = valueFor(baseDs, baseRow, header);
      var tdBase = document.createElement('td');
      var baseSpan = document.createElement('span');
      baseSpan.className = baseVal == null ? 'csz-drawer__val csz-drawer__na' : 'csz-drawer__val';
      baseSpan.textContent = baseVal == null ? '—' : baseVal;
      if (baseVal == null) self._anyMissing = true;
      tdBase.appendChild(baseSpan);
      tr.appendChild(tdBase);

      items.forEach(function (it) {
        var td = document.createElement('td');
        var payload = self.state.data[it.handle];
        var ds = dataset(payload, self.state.tab, self.state.unit);
        var row = rowForSize(ds, self.state.size);
        var val = valueFor(ds, row, header);
        var span = document.createElement('span');
        span.className = val == null ? 'csz-drawer__val csz-drawer__na' : 'csz-drawer__val';
        span.textContent = val == null ? '—' : val;
        td.appendChild(span);
        if (val == null) {
          self._anyMissing = true;
        } else {
          var deltaText = formatDelta(val, baseVal);
          if (deltaText != null) {
            var delta = document.createElement('span');
            delta.className = 'csz-drawer__delta';
            delta.textContent = deltaText;
            td.appendChild(delta);
          }
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });

    /* add-to-bag row (added products only) */
    if (items.length) {
      var atbRow = document.createElement('tr');
      atbRow.className = 'csz-drawer__atb-row';
      atbRow.appendChild(document.createElement('th'));
      atbRow.appendChild(document.createElement('td'));
      items.forEach(function (it) {
        var td = document.createElement('td');
        var payload = self.state.data[it.handle];
        var variant = payload ? variantForSize(payload, self.state.size) : null;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'csz-drawer__atb';
        if (variant && variant.available) {
          btn.textContent = 'add to bag';
          btn.setAttribute('aria-label', 'add ' + (it.title || it.handle) + ' size ' + self.state.size + ' to bag');
          btn.addEventListener('click', function () {
            self.addToBag(variant.id, btn);
          });
        } else {
          btn.textContent = variant ? 'sold out' : 'unavailable';
          btn.disabled = true;
        }
        td.appendChild(btn);
        atbRow.appendChild(td);
      });
      tbody.appendChild(atbRow);
    }

    table.appendChild(tbody);
    wrap.appendChild(table);

    /* mobile swipe hint only when the table actually overflows */
    var swipeHint = qs('[data-csz-swipe-hint]', this.root);
    if (swipeHint) swipeHint.hidden = !(wrap.scrollWidth > wrap.clientWidth + 4);
  };

  CompareDrawer.prototype.renderAddButton = function () {
    var btn = qs('[data-csz-open-picker]', this.root);
    var count = window.CompareStore.count();
    qs('[data-csz-add-count]', this.root).textContent = '(' + count + ' of ' + window.CompareStore.MAX_ITEMS + ')';
    btn.disabled = this.atCap();
  };

  CompareDrawer.prototype.renderFootnote = function () {
    var foot = qs('[data-csz-foot]', this.root);
    var kind = this.state.tab === 'product' ? 'garment' : 'body';
    var unit = this.state.unit === 'cm' ? 'centimeters' : 'inches';
    var text = kind + ' measurements, size ' + (this.state.size || '—') + ', ' + unit;
    if (this._anyMissing) text += ' · — means no data for this size';
    foot.textContent = text + ' · ';

    var base = this.basePayload();
    var htm = base && base.sizeChart && base.sizeChart.howToMeasure;
    var link = qs('[data-csz-measure-toggle]', this.root);
    link.hidden = !htm;
    var measure = qs('[data-csz-measure]', this.root);
    if (htm) {
      measure.textContent = '';
      String(htm).split('\n').forEach(function (line) {
        if (!line.trim()) return;
        var p = document.createElement('p');
        p.textContent = line.trim();
        measure.appendChild(p);
      });
    } else {
      measure.hidden = true;
      link.setAttribute('aria-expanded', 'false');
    }
  };

  /* ---------- add to bag (how-to-wear.liquid pattern) ---------- */

  CompareDrawer.prototype.addToBag = function (variantId, btn) {
    btn.disabled = true;
    var original = btn.textContent;
    btn.textContent = 'adding…';
    var done = function (ok) {
      btn.disabled = false;
      btn.textContent = ok ? 'added ✓' : 'couldn’t add';
      window.setTimeout(function () { btn.textContent = original; }, 2500);
    };
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        try {
          if (window.NativeCart && window.NativeCart.refresh) window.NativeCart.refresh();
          if (window.NativeCart && window.NativeCart.open) window.NativeCart.open();
        } catch (e) { /* cart drawer optional — the item is in the cart regardless */ }
        done(true);
      })
      .catch(function () { done(false); });
  };

  /* ---------- picker (state B) ---------- */

  CompareDrawer.prototype.showView = function (view) {
    var fromPicker = this.state.view === 'picker' && view === 'table';
    this.state.view = view;
    qsa('[data-csz-view]', this.root).forEach(function (el) {
      el.hidden = el.getAttribute('data-csz-view') !== view;
    });
    qs('[data-csz-drawer-title]', this.root).textContent =
      view === 'picker' ? 'add a product' : 'compare sizes';
    if (view === 'picker') {
      this.loadRecommendations();
      this.renderPicker();
      var input = qs('[data-csz-search]', this.root);
      if (input) input.focus();
    } else if (this.state.open) {
      this.render();
      /* keep focus inside the dialog when the picker view is torn down */
      if (fromPicker) {
        var addBtn = qs('[data-csz-open-picker]', this.root);
        if (addBtn && !addBtn.disabled) addBtn.focus();
        else qs('.csz-drawer__close', this.root).focus();
      }
    }
  };

  CompareDrawer.prototype.renderPicker = function () {
    var self = this;
    var count = window.CompareStore.count();
    var cap = window.CompareStore.MAX_ITEMS;
    var capLine = qs('[data-csz-cap]', this.root);
    capLine.textContent = this.atCap()
      ? count + ' of ' + cap + ' products added — remove one to add another'
      : count + ' of ' + cap + ' products added — the comparison updates as you add';

    /* refresh every row's action state (server-rendered + search results) */
    qsa('[data-csz-pick]', this.root).forEach(function (row) {
      self.syncPickRow(row);
    });
  };

  CompareDrawer.prototype.syncPickRow = function (row) {
    var handle = row.getAttribute('data-handle');
    var action = qs('[data-csz-pick-add], .csz-drawer__pick-added, .csz-drawer__pick-none', row);
    if (!action) return;
    if (row.getAttribute('data-csz-no-data') === '1') return; /* terminal state */

    var isAdded = window.CompareStore.hasProduct(handle) || handle === this.baseHandle();
    if (isAdded) {
      var added = document.createElement('span');
      added.className = 'csz-drawer__pick-added';
      added.textContent = handle === this.baseHandle() ? 'this product' : 'added ✓';
      action.parentNode.replaceChild(added, action);
    } else {
      if (action.tagName !== 'BUTTON') {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'csz-drawer__pick-add';
        btn.setAttribute('data-csz-pick-add', '');
        btn.textContent = '+ add';
        action.parentNode.replaceChild(btn, action);
        action = btn;
      }
      action.disabled = this.atCap();
    }
  };

  CompareDrawer.prototype.addFromRow = function (row, btn) {
    var self = this;
    var handle = row.getAttribute('data-handle');
    var title = row.getAttribute('data-title') || '';
    if (!handle || this.atCap()) return;

    btn.disabled = true;
    btn.textContent = 'adding…';

    fetchProduct(handle).then(function (payload) {
      self.state.data[handle] = payload;
      var hasChart = payload.sizeChart && (payload.sizeChart.garment || payload.sizeChart.body);
      if (!hasChart) {
        row.setAttribute('data-csz-no-data', '1');
        var none = document.createElement('span');
        none.className = 'csz-drawer__pick-none';
        none.textContent = 'no size data';
        btn.parentNode.replaceChild(none, btn);
        return;
      }
      var variant = variantForSize(payload, self.state.size);
      var res = window.CompareStore.addProduct({
        handle: handle,
        colorHandle: null,
        color: null,
        title: payload.title || title,
        size: self.state.size,
        variantId: variant ? variant.id : null
      });
      if (res.ok) {
        self.showView('table');
      } else {
        btn.disabled = false;
        btn.textContent = '+ add';
        self.renderPicker();
      }
    }, function () {
      btn.disabled = false;
      btn.textContent = 'couldn’t load — retry';
    });
  };

  /* ---------- predictive search ---------- */

  CompareDrawer.prototype.onSearchInput = function (value) {
    var self = this;
    window.clearTimeout(this._searchTimer);
    var q = value.trim();
    var block = qs('[data-csz-results-block]', this.root);
    var collectionBlock = qs('[data-csz-collection-block]', this.root);

    if (q.length < 2) {
      block.hidden = true;
      /* bring similar styles back only when it actually has rows */
      if (collectionBlock) collectionBlock.hidden = !qs('[data-csz-pick]', collectionBlock);
      return;
    }
    this._searchTimer = window.setTimeout(function () { self.search(q); }, 280);
  };

  CompareDrawer.prototype.search = function (q) {
    var self = this;
    var seq = ++this._searchSeq;
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      })
      .then(function (json) {
        if (seq !== self._searchSeq || self.state.view !== 'picker') return;
        var products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
        self.renderSearchResults(products);
      })
      .catch(function () {
        if (seq !== self._searchSeq) return;
        self.renderSearchResults(null);
      });
  };

  CompareDrawer.prototype.renderSearchResults = function (products) {
    var self = this;
    var block = qs('[data-csz-results-block]', this.root);
    var list = qs('[data-csz-results]', this.root);
    var collectionBlock = qs('[data-csz-collection-block]', this.root);
    block.hidden = false;
    if (collectionBlock) collectionBlock.hidden = true;
    list.textContent = '';

    if (products === null) {
      list.innerHTML = '<li class="csz-drawer__empty">couldn’t search right now — try again.</li>';
      return;
    }
    if (!products.length) {
      list.innerHTML = '<li class="csz-drawer__empty">no products found.</li>';
      return;
    }

    products.forEach(function (p) {
      if (!p.handle) return;
      list.appendChild(self.buildPickRow({
        handle: p.handle,
        title: p.title,
        image: p.featured_image && (p.featured_image.url || p.featured_image),
        type: p.type,
        priceText: formatSearchPrice(p.price)
      }));
    });
  };

  /* one suggestion row (search results + similar styles share this) */
  CompareDrawer.prototype.buildPickRow = function (info) {
    var li = document.createElement('li');
    li.className = 'csz-drawer__pick';
    li.setAttribute('data-csz-pick', '');
    li.setAttribute('data-handle', info.handle);
    li.setAttribute('data-title', info.title || '');

    var thumb = document.createElement('span');
    thumb.className = 'csz-drawer__pick-thumb';
    if (typeof info.image === 'string' && info.image) {
      var img = document.createElement('img');
      img.src = info.image + (info.image.indexOf('?') === -1 ? '?' : '&') + 'width=120';
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    li.appendChild(thumb);

    var infoEl = document.createElement('span');
    infoEl.className = 'csz-drawer__pick-info';
    var name = document.createElement('span');
    name.className = 'csz-drawer__pick-name';
    name.textContent = (info.title || info.handle).toLowerCase();
    infoEl.appendChild(name);
    var sub = document.createElement('span');
    sub.className = 'csz-drawer__pick-sub';
    var bits = [];
    if (info.type) bits.push(String(info.type).toLowerCase());
    if (info.priceText) bits.push(info.priceText);
    sub.textContent = bits.join(' · ');
    infoEl.appendChild(sub);
    li.appendChild(infoEl);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'csz-drawer__pick-add';
    btn.setAttribute('data-csz-pick-add', '');
    btn.textContent = '+ add';
    li.appendChild(btn);

    this.syncPickRow(li);
    return li;
  };

  /* "similar styles": Shopify product recommendations for the base product,
     kept only when comparable (shared garment header), first 5 */
  CompareDrawer.prototype.loadRecommendations = function () {
    var self = this;
    if (this._recsPromise) return this._recsPromise;
    var block = qs('[data-csz-collection-block]', this.root);
    var list = qs('[data-csz-collection]', this.root);
    if (!block || !list || !this.config.baseId) return Promise.resolve();

    block.hidden = false;
    list.innerHTML = '<li class="csz-drawer__empty">finding similar styles…</li>';

    this._recsPromise = fetch(
      '/recommendations/products.json?product_id=' + encodeURIComponent(this.config.baseId) + '&limit=10&intent=related',
      { credentials: 'same-origin' }
    )
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var products = ((json && json.products) || []).filter(function (p) {
          return p.handle && p.handle !== self.baseHandle();
        });
        return fetchProduct(self.baseHandle()).then(function (basePayload) {
          return Promise.all(products.map(function (p) {
            return fetchProduct(p.handle).then(
              function (payload) { return { p: p, payload: payload }; },
              function () { return null; }
            );
          })).then(function (results) {
            var rows = results.filter(Boolean).filter(function (r) {
              return sharesGarmentHeader(basePayload, r.payload);
            }).slice(0, 5);
            list.textContent = '';
            if (!rows.length) {
              block.hidden = true;
              return;
            }
            rows.forEach(function (r) {
              /* rec API prices are cents, unlike predictive search's decimals */
              list.appendChild(self.buildPickRow({
                handle: r.p.handle,
                title: r.p.title || (r.payload && r.payload.title),
                image: r.p.featured_image && (r.p.featured_image.url || r.p.featured_image),
                type: r.p.type || (r.payload && r.payload.subtitle),
                priceText: formatSearchPrice((r.p.price || 0) / 100)
              }));
            });
          });
        });
      })
      .catch(function () {
        list.textContent = '';
        block.hidden = true;
        self._recsPromise = null; /* transient failure — retry on next open */
      });
    return this._recsPromise;
  };

  /* ---------- events ---------- */

  CompareDrawer.prototype.bind = function () {
    var self = this;

    qsa('[data-csz-drawer-close]', this.root).forEach(function (el) {
      el.addEventListener('click', function () { self.close(); });
    });

    document.addEventListener('keydown', function (e) {
      if (!self.state.open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
        return;
      }
      if (e.key === 'Tab') self.trapFocus(e);
    });

    qsa('[data-csz-tab]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.tab = btn.getAttribute('data-csz-tab');
        self.render();
      });
    });
    qsa('[data-csz-unit]', this.root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.unit = btn.getAttribute('data-csz-unit');
        self.render();
      });
    });

    qs('[data-csz-open-picker]', this.root).addEventListener('click', function () {
      self.showView('picker');
    });
    qs('[data-csz-back]', this.root).addEventListener('click', function () {
      self.showView('table');
    });

    var measureToggle = qs('[data-csz-measure-toggle]', this.root);
    measureToggle.addEventListener('click', function () {
      var measure = qs('[data-csz-measure]', self.root);
      measure.hidden = !measure.hidden;
      measureToggle.setAttribute('aria-expanded', measure.hidden ? 'false' : 'true');
    });

    var search = qs('[data-csz-search]', this.root);
    if (search) {
      search.addEventListener('input', function () { self.onSearchInput(search.value); });
    }

    /* delegated: + add buttons in either suggestion list */
    this.root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-csz-pick-add]');
      if (!btn || btn.disabled) return;
      var row = btn.closest('[data-csz-pick]');
      if (row) self.addFromRow(row, btn);
    });

    /* store changes re-render the open drawer (and fetch any new payloads) */
    window.CompareStore.onChange(function () {
      if (!self.state.open) return;
      if (self.state.view === 'picker') {
        self.renderPicker();
      } else {
        self.load();
        self.render();
      }
    });

    this.bindSheetDrag();
  };

  CompareDrawer.prototype.trapFocus = function (e) {
    var focusables = qsa('button:not([disabled]), input, a[href], [tabindex="0"]', this.panel)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* mobile: drag the grabber down to dismiss */
  CompareDrawer.prototype.bindSheetDrag = function () {
    var self = this;
    var grabber = qs('[data-csz-grabber]', this.root);
    if (!grabber || !window.PointerEvent) return;
    var startY = null;

    grabber.style.touchAction = 'none';
    grabber.addEventListener('pointerdown', function (e) {
      startY = e.clientY;
      grabber.setPointerCapture(e.pointerId);
      self.panel.style.transition = 'none';
    });
    grabber.addEventListener('pointermove', function (e) {
      if (startY == null) return;
      var dy = Math.max(0, e.clientY - startY);
      self.panel.style.transform = 'translateY(' + dy + 'px)';
    });
    var end = function (e) {
      if (startY == null) return;
      var dy = Math.max(0, e.clientY - startY);
      startY = null;
      self.panel.style.transition = '';
      self.panel.style.transform = '';
      if (dy > 90) self.close();
    };
    grabber.addEventListener('pointerup', end);
    grabber.addEventListener('pointercancel', end);
  };

  /* ---------- boot ---------- */

  function boot() {
    var root = qs('[data-csz-drawer]');
    var config = window.CompareDrawerConfig;
    if (!root || !config || !window.CompareStore) return;

    var drawer = new CompareDrawer(root, config);
    window.CompareDrawer = drawer;

    qsa('[data-csz-drawer-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        drawer.open(trigger);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
